// Konto-Tab der Einstellungen — Stufe, Passwort ändern, Claude-API-Key.
//
// Stand bis 10.09.2026 als Profil-Blöcke direkt im Profil-Panel, und nur in Study:
// die Blöcke liefen über Studys Backend-Proxy (`profileApi`). Der Auth-Server
// bietet dieselben Routen aber direkt an (`/api/profile`, `/api/profile/password`,
// `/api/profile/apikey`), und seine CORS-Regel lässt alle drei App-Origins mit
// Credentials zu — deshalb spricht dieser Tab mednos.eu direkt an und steht in
// Radiology und Papers genauso zur Verfügung.
//
// Anders als die übrigen Tabs der Konsole ist dieser NICHT admin-only: das
// Passwort gehört zum Konto, nicht zur Administration.

import { authApi, authJsonPost, ppLabel, ppStatus, ppHint, ppButton, ppLink } from './platform-api.js';

function fehlerText(r) {
  if (r.body?.error) return r.body.error;
  if (r.status === 401) return 'Nicht angemeldet.';
  return 'Profil derzeit nicht erreichbar.';
}

function keyStatusText(set, tail) {
  return set ? `gesetzt · …${tail || ''}` : 'nicht gesetzt';
}

/**
 * Anzeigetext für die EFFEKTIVE Stufe — dieselbe Regel wie backend-seitig
 * `user_llm::resolve`/`is_premium` (apps/study/backend/src/llm/user_llm.rs):
 * ein Admin zählt unabhängig vom gespeicherten `tier` als Premium.
 */
function tierText(tier, role) {
  if (tier === 'premium') return 'Premium';
  if (role === 'admin') return 'Premium (Admin)';
  return 'Freemium';
}

function abschnitt(...kinder) {
  const el = document.createElement('div');
  el.className = 'mednos-admin__section';
  el.append(...kinder);
  return el;
}

function passwortFeld(placeholder, autocomplete) {
  const i = document.createElement('input');
  i.className = 'profile-panel__input';
  i.type = 'password';
  i.placeholder = placeholder;
  i.autocomplete = autocomplete;
  return i;
}

function passwortAbschnitt(profile) {
  // Konto ohne Passwort-Hash: kein Formular, klarer Hinweis.
  if (!profile.has_password) {
    return abschnitt(ppLabel('Passwort'), ppHint('Für dieses Konto ist kein Passwort hinterlegt.'));
  }
  const alt = passwortFeld('Aktuelles Passwort', 'current-password');
  const neu1 = passwortFeld('Neues Passwort (min. 8 Zeichen)', 'new-password');
  const neu2 = passwortFeld('Neues Passwort wiederholen', 'new-password');
  const status = ppStatus('', false);
  const setStatus = (text, isErr) => {
    status.textContent = text;
    status.classList.toggle('profile-panel__status--err', !!isErr);
  };
  const speichern = ppButton('Passwort ändern', async () => {
    if (neu1.value !== neu2.value) {
      setStatus('Neue Passwörter stimmen nicht überein.', true);
      return;
    }
    const r = await authJsonPost('/api/profile/password', {
      old_password: alt.value,
      new_password: neu1.value,
    });
    if (r.status !== 200) {
      setStatus(fehlerText(r), true);
      return;
    }
    [alt, neu1, neu2].forEach((f) => { f.value = ''; });
    setStatus('Passwort geändert ✓', false);
  });
  return abschnitt(ppLabel('Passwort'), alt, neu1, neu2, speichern, status);
}

function keyAbschnitt(profile) {
  const status = ppStatus(keyStatusText(profile.api_key_set, profile.api_key_tail), false);
  const feld = document.createElement('input');
  feld.className = 'profile-panel__input';
  feld.type = 'password';
  feld.placeholder = 'sk-ant-…';
  feld.autocomplete = 'off';
  feld.spellcheck = false;

  const zeile = document.createElement('div');
  zeile.className = 'profile-panel__keyrow';

  const anwenden = (set, tail) => {
    status.textContent = keyStatusText(set, tail);
    status.classList.remove('profile-panel__status--err');
    loeschen.hidden = !set;
    speichern.textContent = set ? 'Ersetzen' : 'Speichern';
  };
  const senden = async (wert) => {
    const r = await authJsonPost('/api/profile/apikey', { api_key: wert });
    if (r.status !== 200) {
      status.textContent = fehlerText(r);
      status.classList.add('profile-panel__status--err');
      return;
    }
    feld.value = '';
    anwenden(r.body?.api_key_set, r.body?.api_key_tail);
  };

  const speichern = ppButton(profile.api_key_set ? 'Ersetzen' : 'Speichern', () => {
    const wert = feld.value.trim();
    if (wert) senden(wert);
  });
  // Leerer Wert löscht den Key — so will es die Route.
  const loeschen = ppLink('Key löschen', () => senden(''));
  loeschen.hidden = !profile.api_key_set;

  zeile.append(feld, speichern);
  return abschnitt(ppLabel('Claude API-Key'), status, zeile, loeschen);
}

/** Baut den Konto-Tab in `container` und lädt das Profil. */
export function renderKontoPane(container) {
  container.replaceChildren(ppHint('Lade Profil…'));
  authApi('/api/profile').then((r) => {
    if (r.status !== 200 || !r.body) {
      container.replaceChildren(ppHint(fehlerText(r)));
      return;
    }
    const profile = r.body;
    const trenner = () => {
      const d = document.createElement('div');
      d.className = 'profile-panel__divider';
      return d;
    };
    // Schmal halten: die Konsole ist 56 rem breit, Passwortfelder über die volle
    // Breite läsen sich wie ein Formular für einen Roman.
    const spalte = document.createElement('div');
    spalte.className = 'mednos-konto';
    spalte.append(
      abschnitt(ppLabel('Stufe'), ppStatus(tierText(profile.tier, profile.role), false)),
      trenner(),
      passwortAbschnitt(profile),
      trenner(),
      keyAbschnitt(profile),
    );
    container.replaceChildren(spalte);
  });
}

export const KONTO_CSS = `
.mednos-konto { display: flex; flex-direction: column; gap: 16px; max-width: 22rem; }
`;
